<script setup lang="ts">
import { useRoute } from 'vue-router'
import { onMounted, ref } from 'vue'
import FormViewer from '@/domains/home/services/nir/forms/components/FormViewer.vue'
import router from '@/router'
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import jwtService from "@/app/services/jwt.service";
import AppModal from "@/components/ui/AppModal.vue";
import { useNotificationsStore } from "@/app/notify/notifications.ts";
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import type {AxiosError} from "axios";
import {ApiErrorResponse} from "@/app/types";

const route = useRoute()
const store = useFormsStore()
const notify = useNotificationsStore()
const institutionStore = useInstitutionStore()
const loading = ref(false)
const approving = ref(false)
const publishing = ref(false)
const enablingDirectAdmission = ref(false)
const disablingDirectAdmission = ref(false)
const enablingInterimErn = ref(false)
const disablingInterimErn = ref(false)
const formId = ref("")
const showApproveConfirm = ref(false)
const showPublishConfirm = ref(false)
const showEnableDirectAdmissionConfirm = ref(false)
const showDisableDirectAdmissionConfirm = ref(false)
const showEnableInterimErnConfirm = ref(false)
const showDisableInterimErnConfirm = ref(false)

onMounted(() => {
  formId.value = route.params.id as string
  store.fetchFormFields(formId.value)
  store.fetchFormDefinition(formId.value)
  store.fetchFields(1, 1000)
  fetch()
})

function fetch() {

  store.fetchForm(formId.value).
  then(() => {
    institutionStore.fetchInstitution(store.form?.entityId || "")
  })

  loading.value = true
  store.fetchFormFields(formId.value).then(() => {
    loading.value = false
  })
}

function back() {
  router.push({ name: 'nir-forms-list' })
}

function approve(){
  showApproveConfirm.value = true
}

function publish(){
  showPublishConfirm.value = true
}

function confirmApprove(){
  let userId  = jwtService.getUser()["userId"]
  showApproveConfirm.value = false
  approving.value = true
  store.approve({id:formId.value, userId:userId})
      .then(() => {
        approving.value = false
        window.location.reload()
      })
      .catch((error:AxiosError<ApiErrorResponse>) => {
        notify.error(error.response?.data?.error.error ?? "Error approving");
      })
}

function confirmPublish(){
  let userId  = jwtService.getUser()["userId"]
  showPublishConfirm.value = false
  publishing.value = true
  store.publish({id:formId.value, userId:userId})
      .then(() => {
        publishing.value = false
        window.location.reload()
      })
      .catch((error:AxiosError<ApiErrorResponse>) => {
        notify.error(error.response?.data?.error.error ?? "Error publishing");
      })
}

function enableDirectAdmission() {
  showEnableDirectAdmissionConfirm.value = true
}

function confirmEnableDirectAdmission() {
  enablingDirectAdmission.value = true
  store.enableDirectAdmission({ formId: formId.value })
      .then(() => {
        enablingDirectAdmission.value = false
        showEnableDirectAdmissionConfirm.value = false
        notify.success("Direct admission enabled successfully")
        store.fetchForm(formId.value)
      })
      .catch(() => {
        enablingDirectAdmission.value = false
        showEnableDirectAdmissionConfirm.value = false
        notify.error("Failed to enable direct admission")
      })
}

function disableDirectAdmission() {
  showDisableDirectAdmissionConfirm.value = true
}

function confirmDisableDirectAdmission() {
  disablingDirectAdmission.value = true
  store.disableDirectAdmission({ formId: formId.value })
      .then(() => {
        disablingDirectAdmission.value = false
        showDisableDirectAdmissionConfirm.value = false
        notify.success("Direct admission disabled successfully")
        store.fetchForm(formId.value)
      })
      .catch(() => {
        disablingDirectAdmission.value = false
        showDisableDirectAdmissionConfirm.value = false
        notify.error("Failed to disable direct admission")
      })
}

function enableInterimErn() {
  showEnableInterimErnConfirm.value = true
}

function confirmEnableInterimErn() {
  enablingInterimErn.value = true
  store.enableInterimErn({ formId: formId.value })
      .then(() => {
        enablingInterimErn.value = false
        showEnableInterimErnConfirm.value = false
        notify.success("Interim ERN enabled successfully")
        store.fetchForm(formId.value)
      })
      .catch(() => {
        enablingInterimErn.value = false
        showEnableInterimErnConfirm.value = false
        notify.error("Failed to enable interim ERN")
      })
}

function disableInterimErn() {
  showDisableInterimErnConfirm.value = true
}

function confirmDisableInterimErn() {
  disablingInterimErn.value = true
  store.disableInterimErn({ formId: formId.value })
      .then(() => {
        disablingInterimErn.value = false
        showDisableInterimErnConfirm.value = false
        notify.success("Interim ERN disabled successfully")
        store.fetchForm(formId.value)
      })
      .catch(() => {
        disablingInterimErn.value = false
        showDisableInterimErnConfirm.value = false
        notify.error("Failed to disable interim ERN")
      })
}
</script>

<template>
  <div class="flex items-center justify-center z-50">
    <div class="max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
      <div class="flex justify-between py-2">
        <button class="btn-primary " @click="back">
          <i class="fa-solid fa-arrow-left"></i> Back to Forms
        </button>

        <div class="flex gap-2" v-if="store.form">
          <button class="btn-primary " @click="approve" :disabled="store.form?.approvedAt != null">
            <i class="fa-solid fa-check-circle"></i> Approve
            <span class="lds-ring" v-if="approving">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </span>
          </button>
          <button class="btn-success" @click="publish" :disabled="store.form?.approvedAt == null || store.form?.publishedAt != null">
            <i class="fa-solid fa-cloud"></i> Publish
            <span class="lds-ring" v-if="publishing">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </span>
          </button>

          <div v-if="institutionStore.institution && institutionStore.institution.hasDirectAdmission">
            <button
                v-if="!store.form.hasDirectAdmission"
                @click="enableDirectAdmission"
                class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700">
              <i class="fa fa-bolt mr-2"></i>Enable Direct Admission
            </button>
            <button
                v-else
                @click="disableDirectAdmission"
                class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
              <i class="fa fa-ban mr-2"></i>Disable Direct Admission
            </button>
          </div>
          <div v-if="institutionStore.institution && institutionStore.institution.hasInterimErn">
            <button
                v-if="!store.form.issuesInterimErn"
                @click="enableInterimErn"
                class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700">
              <i class="fa fa-id-card mr-2"></i>Enable Interim ERN
            </button>
            <button
                v-else
                @click="disableInterimErn"
                class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
              <i class="fa fa-ban mr-2"></i>Disable Interim ERN
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-4 gap-2">
        <div class="col-span-3">
          <FormViewer :formDefinition="store.formDefinition" :active="false"/>
        </div>
        <div class="flex flex-col border border-gray-200 rounded-lg p-2" v-if="store.formData">
          <div class="flex flex-col border-b border-gray-100 py-1" v-for="(section, idx) in store.formData.sections" :key="idx">
            <span class="text-xs font-semibold">{{section.name}}</span>
            <div class="flex gap-2 text-[10px] py-0.5" v-for="(field, fIdx) in section.fields" :key="fIdx">
              {{fIdx + 1}}. {{field.field.name}} <label class="bg-primary-50 rounded px-1 text-primary">{{field.field.type}}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Approve Confirmation Modal -->
  <AppModal v-model="showApproveConfirm" xl>
    <div class="p-6">
      <div class="flex items-start mb-4">
        <div class="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          <i class="fa fa-check-circle text-2xl text-blue-600"></i>
        </div>
        <div class="ml-4 flex-1">
          <h3 class="text-xl font-bold text-gray-900 mb-2">Approve Form</h3>
          <div class="text-sm text-gray-600 space-y-2">
            <p class="font-semibold text-gray-800">
              <i class="fa fa-exclamation-triangle text-orange-500 mr-2"></i>
              This is a critical action!
            </p>
            <p>You are about to <strong>approve</strong> this form configuration.</p>
            <p>Once approved:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>The form will be marked as approved in the system</li>
              <li>The form can then be published for public use</li>
              <li>This indicates the form structure has been reviewed and validated</li>
            </ul>
            <p class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <i class="fa fa-info-circle text-yellow-600 mr-2"></i>
              Please ensure you have reviewed all form fields and configurations before proceeding.
            </p>
          </div>
        </div>
      </div>

      <div class="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
        <button
          @click="showApproveConfirm = false"
          class="btn-danger">
          <i class="fas fa-times mr-2"></i>
          Cancel
        </button>
        <button
          @click="confirmApprove"
          class="btn-primary bg-blue-600 hover:bg-blue-700">
          <i class="fa fa-check-circle mr-2"></i>
          Yes, Approve Form
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Publish Confirmation Modal -->
  <AppModal v-model="showPublishConfirm" xl>
    <div class="p-6">
      <div class="flex items-start mb-4">
        <div class="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <i class="fa fa-cloud text-2xl text-green-600"></i>
        </div>
        <div class="ml-4 flex-1">
          <h3 class="text-xl font-bold text-gray-900 mb-2">Publish Form</h3>
          <div class="text-sm text-gray-600 space-y-2">
            <p class="font-semibold text-gray-800">
              <i class="fa fa-exclamation-triangle text-orange-500 mr-2"></i>
              This is a critical action that will make the form publicly available!
            </p>
            <p>You are about to <strong>publish</strong> this form configuration.</p>
            <p>Once published:</p>
            <ul class="list-disc list-inside ml-4 space-y-1">
              <li>The form will be <strong>publicly available</strong> for use</li>
              <li>Users will be able to submit applications using this form</li>
              <li>The form will appear in the active forms list</li>
              <li>Changes to the form after publishing may affect ongoing applications</li>
            </ul>
            <p class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <i class="fa fa-info-circle text-yellow-600 mr-2"></i>
              Ensure the form is fully tested and ready for production use before publishing.
            </p>
          </div>
        </div>
      </div>

      <div class="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
        <button
          @click="showPublishConfirm = false"
          class="btn-danger">
          <i class="fas fa-times mr-2"></i>
          Cancel
        </button>
        <button
          @click="confirmPublish"
          class="btn-success bg-green-600 hover:bg-green-700">
          <i class="fa fa-cloud mr-2"></i>
          Yes, Publish Form
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Enable Direct Admission Confirmation Modal -->
  <AppModal v-model="showEnableDirectAdmissionConfirm" title="Enable Direct Admission" xl2>
    <div class="text-center">
      <p class="mb-4">Are you sure you want to enable direct admission for this form?</p>
      <p class="text-sm text-primary mb-6">Entities submitted using this form will be directly admitted without requiring application processing.</p>
      <div class="flex justify-center gap-3">
        <button @click="showEnableDirectAdmissionConfirm = false" class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
          @click="confirmEnableDirectAdmission"
          :disabled="enablingDirectAdmission"
          class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700 disabled:opacity-50">
          <span v-if="enablingDirectAdmission">Enabling...</span>
          <span v-else>Confirm Enable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Disable Direct Admission Confirmation Modal -->
  <AppModal v-model="showDisableDirectAdmissionConfirm" title="Disable Direct Admission" xl2>
    <div class="text-center">
      <p class="mb-4">Are you sure you want to disable direct admission for this form?</p>
      <p class="text-sm text-gray-500 mb-6">Entities submitted using this form will require application processing before being admitted.</p>
      <div class="flex justify-center gap-3">
        <button @click="showDisableDirectAdmissionConfirm = false" class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
          @click="confirmDisableDirectAdmission"
          :disabled="disablingDirectAdmission"
          class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
          <span v-if="disablingDirectAdmission">Disabling...</span>
          <span v-else>Confirm Disable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Enable Interim ERN Confirmation Modal -->
  <AppModal v-model="showEnableInterimErnConfirm" title="Enable Interim ERN" xl2>
    <div class="text-center">
      <p class="mb-4">Are you sure you want to enable interim ERN for this form?</p>
      <p class="text-sm text-primary mb-6">This form will be allowed to issue interim Entity Registration Numbers before full verification is complete.</p>
      <div class="flex justify-center gap-3">
        <button @click="showEnableInterimErnConfirm = false" class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
          @click="confirmEnableInterimErn"
          :disabled="enablingInterimErn"
          class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700 disabled:opacity-50">
          <span v-if="enablingInterimErn">Enabling...</span>
          <span v-else>Confirm Enable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Disable Interim ERN Confirmation Modal -->
  <AppModal v-model="showDisableInterimErnConfirm" title="Disable Interim ERN" xl2>
    <div class="text-center">
      <p class="mb-4">Are you sure you want to disable interim ERN for this form?</p>
      <p class="text-sm text-gray-500 mb-6">This form will no longer issue interim Entity Registration Numbers.</p>
      <div class="flex justify-center gap-3">
        <button @click="showDisableInterimErnConfirm = false" class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
          @click="confirmDisableInterimErn"
          :disabled="disablingInterimErn"
          class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
          <span v-if="disablingInterimErn">Disabling...</span>
          <span v-else>Confirm Disable</span>
        </button>
      </div>
    </div>
  </AppModal>

</template>

<style scoped></style>
